<template>
  <div class="kw-phone">
    <KwDropdown :show="show" @close="() => show && close()">
      <KwInput
        slot='toggle'
        type="tel"
        pattern="^\+[0-9\s]+"
        v-phone-number
        :value="data"
        :invalid="invalid"
        :valid="valid"
        :placeholder="'+' + activeCountry.dialCode + ' ' + activeCountry.dialNumber"
        @focus="phoneNumberFocus"
        @input="phoneNumberChange"
        @click:icon-prefix='toggleList'
        >
          <span class="kw-phone-flag" slot="icon-prefix">
            <i :class="'flag flag-' + activeCountry.iso2.toLowerCase()"/>
          </span>
      </KwInput>
      <div class="kw-phone-list">
        <div
          class="kw-phone-number"
          v-for="(country, index) in allCountries"
          :key="index"
          @click="() => select(country)"
        >
          <i :class="'flag flag-' + country.iso2.toLowerCase()"/>
          <span class="kw-phone-country">
          {{ country.name }}
          </span>
          <span class="kw-phone-code">
          +{{ country.dialCode }}
          </span>
        </div>
      </div>
    </KwDropdown>
  </div>
</template>

<script>
import KwSvgicon from '@kiwitaxi/vue-components/dist/KwSvgicon';
import KwInput from '@kiwitaxi/vue-components/dist/KwInput';
// eslint-disable-next-line import/extensions
import { AsYouType, formatIncompletePhoneNumber } from 'libphonenumber-js';
import KwDropdown from '@kiwitaxi/vue-components/dist/KwDropdown';
import { AllCountries } from '../../utils';
import { CurrentCountry } from '../../services';
import { PhoneNumber } from '../../directives';

export default {
  name: 'KwPhoneInput',
  directives: {
    PhoneNumber,
  },
  components: {
    KwDropdown,
    KwInput,
    KwSvgicon,
  },
  props: {
    value: [String, Number, Object],
    disabled: {
      type: Boolean,
      default: false,
    },
    valid: {
      type: Boolean,
      required: false,
      default: true,
    },
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      allCountries: AllCountries,
      activeCountry: AllCountries[0],
      data: this.value,
    };
  },
  watch: {
    data(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal.length > 0) {
          this.data = this.formattedResult;
        }
        this.$emit('input', this.data);
      }
    },
    value(newVal) {
      this.data = newVal;
    },
  },
  mounted() {
    let countryPromise;
    if (window.navigator.language) {
      const code = window.navigator.language.split('-')[1];
      countryPromise = Promise.resolve(code.toUpperCase());
    } else {
      countryPromise = CurrentCountry();
    }
    countryPromise
      .then(response => {
        if (response) {
          this.activeCountry = this.allCountries.find(country =>
            country.iso2.toUpperCase() === response);
        } else {
          [this.activeCountry] = this.allCountries;
        }
        if (this.data) {
          this.data = this.formattedResult;
        }
      })
      .catch(() => {
        [this.activeCountry] = this.allCountries;
        if (this.data) {
          this.data = this.formattedResult;
        }
      });
  },
  methods: {
    phoneNumberFocus() {
      if (this.data === null) {
        this.data = '';
      }
    },
    phoneNumberChange(value) {
      if (value.length) {
        if (value[0] === '+') {
          this.data = value;
        } else {
          this.data = `+${value}`;
        }
      } else {
        this.data = '';
      }
    },
    toggleList() {
      this.show = true;
    },
    select(country) {
      this.activeCountry = country;
      this.data = '';
      this.show = false;
    },
    close() {
      this.show = false;
    },
  },
  computed: {
    formattedResult() {
      if (this.data && this.data[0] === '+') {
        const formatter = new AsYouType(); // eslint-disable-line
        formatter.input(this.data);
        // eslint-disable-next-line max-len
        this.activeCountry = this.allCountries.find(country =>
          country.iso2.toUpperCase() === formatter.country) || this.activeCountry;
      }
      return formatIncompletePhoneNumber(this.data);
    },
  },
};
</script>

<style lang="scss">
.kw-input__icon {
  // z-index: 998;
  cursor: pointer;
}
.kw-phone {
  &-number {
    display: block;
    padding: rem(8);
    white-space: nowrap;
    @include font-size(12);
    text-decoration: none;
    cursor: pointer;
    &:hover,
    &.active {
      background: $success;
      .kw-phone-country,
      .kw-phone-code {
        color: #ffffff;
      }
    }
  }
  &-country {
    padding: 0 rem(8);
    color: $base-light;
  }
  &-code {
    @include font-weight(bold);
    color: $base;
  }
  &-list {
    z-index: 1001;
  }
}

.flag {
  display: inline-block;
  border: 1px solid $base-lighter;
  width: 20px;
  height: 14px;
  background: url("../../assets/images/flags.png") no-repeat;
}

.flag.flag-ph {
  background-position: -216px -120px;
}

.flag.flag-hn {
  background-position: -234px -60px;
}

.flag.flag-je {
  background-position: -180px -72px;
}

.flag.flag-il {
  background-position: -54px -72px;
}

.flag.flag-sg {
  background-position: -18px -144px;
}

.flag.flag-bw {
  background-position: -18px -24px;
}

.flag.flag-jo {
  background-position: -216px -72px;
}

.flag.flag-gp {
  background-position: -54px -60px;
}

.flag.flag-jp {
  background-position: -234px -72px;
}

.flag.flag-ir {
  background-position: -126px -72px;
}

.flag.flag-zw {
  background-position: -36px -180px;
}

.flag.flag-bg {
  background-position: -90px -12px;
}

.flag.flag-ye {
  background-position: -252px -168px;
}

.flag.flag-fr {
  background-position: -126px -48px;
}

.flag.flag-to {
  background-position: -162px -156px;
}

.flag.flag-vc {
  background-position: -108px -168px;
}

.flag.flag-tm {
  background-position: -126px -156px;
}

.flag.flag-hr {
  background-position: -252px -60px;
}

.flag.flag-bv {
  background-position: 0 -24px;
}

.flag.flag-km {
  background-position: -36px -84px;
}

.flag.flag-fj {
  background-position: -54px -48px;
}

.flag.flag-es {
  background-position: -270px -36px;
}

.flag.flag-si {
  background-position: -54px -144px;
}

.flag.flag-qa {
  background-position: -108px -132px;
}

.flag.flag-bs {
  background-position: -252px -12px;
}

.flag.flag-bf {
  background-position: -72px -12px;
}

.flag.flag-af {
  background-position: -36px 0;
}

.flag.flag-ky {
  background-position: -126px -84px;
}

.flag.flag-pg {
  background-position: -198px -120px;
}

.flag.flag-tk {
  background-position: -90px -156px;
}

.flag.flag-gm {
  background-position: -18px -60px;
}

.flag.flag-sd {
  background-position: -270px -132px;
}

.flag.flag-ng {
  background-position: -270px -108px;
}

.flag.flag-eh {
  background-position: -234px -36px;
}

.flag.flag-va {
  background-position: -90px -168px;
}

.flag.flag-ru {
  background-position: -180px -132px;
}

.flag.flag-au {
  background-position: -216px 0;
}

.flag.flag-mt {
  background-position: -72px -108px;
}

.flag.flag-ne {
  background-position: -234px -108px;
}

.flag.flag-sm {
  background-position: -126px -144px;
}

.flag.flag-bm {
  background-position: -180px -12px;
}

.flag.flag-cn {
  background-position: -234px -24px;
}

.flag.flag-mp {
  background-position: 0 -108px;
}

.flag.flag-ba {
  background-position: 0 -12px;
}

.flag.flag-er {
  background-position: -252px -36px;
}

.flag.flag-ag {
  background-position: -54px 0;
}

.flag.flag-hm {
  background-position: -216px -60px;
}

.flag.flag-gt {
  background-position: -126px -60px;
}

.flag.flag-pa {
  background-position: -144px -120px;
}

.flag.flag-jm {
  background-position: -198px -72px;
}

.flag.flag-gf {
  background-position: -216px -48px;
}

.flag.flag-tw {
  background-position: -234px -156px;
}

.flag.flag-yt {
  background-position: -270px -168px;
}

.flag.flag-vg {
  background-position: -144px -168px;
}

.flag.flag-gq {
  background-position: -72px -60px;
}

.flag.flag-ml {
  background-position: -216px -96px;
}

.flag.flag-cm {
  background-position: -216px -24px;
}

.flag.flag-lv {
  background-position: -36px -96px;
}

.flag.flag-dj {
  background-position: -90px -36px;
}

.flag.flag-mr {
  background-position: -36px -108px;
}

.flag.flag-ua {
  background-position: -270px -156px;
}

.flag.flag-am {
  background-position: -108px 0;
}

.flag.flag-cf {
  background-position: -108px -24px;
}

.flag.flag-mk {
  background-position: -198px -96px;
}

.flag.flag-gd {
  background-position: -180px -48px;
}

.flag.flag-tz {
  background-position: -252px -156px;
}

.flag.flag-st {
  background-position: -198px -144px;
}

.flag.flag-bo {
  background-position: -216px -12px;
}

.flag.flag-ci {
  background-position: -162px -24px;
}

.flag.flag-sl {
  background-position: -108px -144px;
}

.flag.flag-mc {
  background-position: -90px -96px;
}

.flag.flag-hu {
  background-position: 0 -72px;
}

.flag.flag-wf {
  background-position: -216px -168px;
}

.flag.flag-gr {
  background-position: -90px -60px;
}

.flag.flag-pm {
  background-position: -270px -120px;
}

.flag.flag-li {
  background-position: -216px -84px;
}

.flag.flag-cd {
  background-position: -90px -24px;
}

.flag.flag-pl {
  background-position: -252px -120px;
}

.flag.flag-lb {
  background-position: -180px -84px;
}

.flag.flag-sr {
  background-position: -180px -144px;
}

.flag.flag-co {
  background-position: -252px -24px;
}

.flag.flag-gl {
  background-position: 0 -60px;
}

.flag.flag-tf {
  background-position: -18px -156px;
}

.flag.flag-ws {
  background-position: -234px -168px;
}

.flag.flag-sa {
  background-position: -216px -132px;
}

.flag.flag-eu {
  background-position: -18px -48px;
}

.flag.flag-cr {
  background-position: -270px -24px;
}

.flag.flag-bi {
  background-position: -126px -12px;
}

.flag.flag-mn {
  background-position: -252px -96px;
}

.flag.flag-ee {
  background-position: -198px -36px;
}

.flag.flag-pt {
  background-position: -54px -132px;
}

.flag.flag-so {
  background-position: -162px -144px;
}

.flag.flag-tv {
  background-position: -216px -156px;
}

.flag.flag-hk {
  background-position: -198px -60px;
}

.flag.flag-ke {
  background-position: -252px -72px;
}

.flag.flag-mz {
  background-position: -180px -108px;
}

.flag.flag-cl {
  background-position: -198px -24px;
}

.flag.flag-vn {
  background-position: -180px -168px;
}

.flag.flag-lk {
  background-position: -234px -84px;
}

.flag.flag-dm {
  background-position: -126px -36px;
}

.flag.flag-tl {
  background-position: -108px -156px;
}

.flag.flag-nz {
  background-position: -108px -120px;
}

.flag.flag-fi {
  background-position: -36px -48px;
}

.flag.flag-om {
  background-position: -126px -120px;
}

.flag.flag-pw {
  background-position: -72px -132px;
}

.flag.flag-tj {
  background-position: -72px -156px;
}

.flag.flag-bt {
  background-position: -270px -12px;
}

.flag.flag-io {
  background-position: -90px -72px;
}

.flag.flag-pn {
  background-position: 0 -132px;
}

.flag.flag-br {
  background-position: -234px -12px;
}

.flag.flag-gh {
  background-position: -252px -48px;
}

.flag.flag-tn {
  background-position: -144px -156px;
}

.flag.flag-mg {
  background-position: -162px -96px;
}

.flag.flag-tc {
  background-position: -270px -144px;
}

.flag.flag-lc {
  background-position: -198px -84px;
}

.flag.flag-uz {
  background-position: -72px -168px;
}

.flag.flag-dk {
  background-position: -108px -36px;
}

.flag.flag-za {
  background-position: 0 -180px;
}

.flag.flag-me {
  background-position: -126px -96px;
}

.flag.flag-nl {
  background-position: -18px -120px;
}

.flag.flag-cv {
  background-position: -18px -36px;
}

.flag.flag-sz {
  background-position: -252px -144px;
}

.flag.flag-pr {
  background-position: -18px -132px;
}

.flag.flag-nr {
  background-position: -72px -120px;
}

.flag.flag-no {
  background-position: -36px -120px;
}

.flag.flag-de {
  background-position: -72px -36px;
}

.flag.flag-kw {
  background-position: -108px -84px;
}

.flag.flag-cg {
  background-position: -126px -24px;
}

.flag.flag-th {
  background-position: -54px -156px;
}

.flag.flag-bb {
  background-position: -18px -12px;
}

.flag.flag-tg {
  background-position: -36px -156px;
}

.flag.flag-ax {
  background-position: -252px 0;
}

.flag.flag-lr {
  background-position: -252px -84px;
}

.flag.flag-kh {
  background-position: 0 -84px;
}

.flag.flag-ai {
  background-position: -72px 0;
}

.flag.flag-us {
  background-position: -36px -168px;
}

.flag.flag-td {
  background-position: 0 -156px;
}

.flag.flag-it {
  background-position: -162px -72px;
}

.flag.flag-rw {
  background-position: -198px -132px;
}

.flag.flag-se {
  background-position: 0 -144px;
}

.flag.flag-py {
  background-position: -90px -132px;
}

.flag.flag-lu {
  background-position: -18px -96px;
}

.flag.flag-et {
  background-position: 0 -48px;
}

.flag.flag-sv {
  background-position: -216px -144px;
}

.flag.flag-ki {
  background-position: -18px -84px;
}

.flag.flag-sn {
  background-position: -144px -144px;
}

.flag.flag-in {
  background-position: -72px -72px;
}

.flag.flag-sy {
  background-position: -234px -144px;
}

.flag.flag-ms {
  background-position: -54px -108px;
}

.flag.flag-kz {
  background-position: -144px -84px;
}

.flag.flag-tr {
  background-position: -180px -156px;
}

.flag.flag-kr {
  background-position: -90px -84px;
}

.flag.flag-ar {
  background-position: -162px 0;
}

.flag.flag-nf {
  background-position: -252px -108px;
}

.flag.flag-ch {
  background-position: -144px -24px;
}

.flag.flag-ht {
  background-position: -270px -60px;
}

.flag.flag-pf {
  background-position: -180px -120px;
}

.flag.flag-bj {
  background-position: -144px -12px;
}

.flag.flag-mf {
  background-position: -144px -96px;
}

.flag.flag-at {
  background-position: -198px 0;
}

.flag.flag-id {
  background-position: -18px -72px;
}

.flag.flag-sh {
  background-position: -36px -144px;
}

.flag.flag-mm {
  background-position: -234px -96px;
}

.flag.flag-bh {
  background-position: -108px -12px;
}

.flag.flag-al {
  background-position: -90px 0;
}

.flag.flag-bz {
  background-position: -54px -24px;
}

.flag.flag-ie {
  background-position: -36px -72px;
}

.flag.flag-cu {
  background-position: 0 -36px;
}

.flag.flag-gg {
  background-position: -234px -48px;
}

.flag.flag-kn {
  background-position: -54px -84px;
}

.flag.flag-be {
  background-position: -54px -12px;
}

.flag.flag-my {
  background-position: -162px -108px;
}

.flag.flag-ao {
  background-position: -144px 0;
}

.flag.flag-ga {
  background-position: -144px -48px;
}

.flag.flag-cy {
  background-position: -36px -36px;
}

.flag.flag-do {
  background-position: -144px -36px;
}

.flag.flag-gn {
  background-position: -36px -60px;
}

.flag.flag-re {
  background-position: -126px -132px;
}

.flag.flag-mx {
  background-position: -144px -108px;
}

.flag.flag-na {
  background-position: -198px -108px;
}

.flag.flag-gi {
  background-position: -270px -48px;
}

.flag.flag-sk {
  background-position: -90px -144px;
}

.flag.flag-bn {
  background-position: -198px -12px;
}

.flag.flag-ve {
  background-position: -126px -168px;
}

.flag.flag-sj {
  background-position: -72px -144px;
}

.flag.flag-ro {
  background-position: -144px -132px;
}

.flag.flag-vi {
  background-position: -162px -168px;
}

.flag.flag-by {
  background-position: -36px -24px;
}

.flag.flag-ae {
  background-position: -18px 0;
}

.flag.flag-nc {
  background-position: -216px -108px;
}

.flag.flag-fm {
  background-position: -90px -48px;
}

.flag.flag-ni {
  background-position: 0 -120px;
}

.flag.flag-pk {
  background-position: -234px -120px;
}

.flag.flag-uy {
  background-position: -54px -168px;
}

.flag.flag-bl {
  background-position: -162px -12px;
}

.flag.flag-mu {
  background-position: -90px -108px;
}

.flag.flag-kp {
  background-position: -72px -84px;
}

.flag.flag-mv {
  background-position: -108px -108px;
}

.flag.flag-ck {
  background-position: -180px -24px;
}

.flag.flag-an {
  background-position: -126px 0;
}

.flag.flag-tt {
  background-position: -198px -156px;
}

.flag.flag-is {
  background-position: -144px -72px;
}

.flag.flag-vu {
  background-position: -198px -168px;
}

.flag.flag-dz {
  background-position: -162px -36px;
}

.flag.flag-pe {
  background-position: -162px -120px;
}

.flag.flag-la {
  background-position: -162px -84px;
}

.flag.flag-np {
  background-position: -54px -120px;
}

.flag.flag-mh {
  background-position: -180px -96px;
}

.flag.flag-mo {
  background-position: -270px -96px;
}

.flag.flag-gy {
  background-position: -180px -60px;
}

.flag.flag-ma {
  background-position: -72px -96px;
}

.flag.flag-ug {
  background-position: 0 -168px;
}

.flag.flag-fo {
  background-position: -108px -48px;
}

.flag.flag-md {
  background-position: -108px -96px;
}

.flag.flag-ad {
  background-position: 0 0;
}

.flag.flag-ca {
  background-position: -72px -24px;
}

.flag.flag-gs {
  background-position: -108px -60px;
}

.flag.flag-mq {
  background-position: -18px -108px;
}

.flag.flag-mw {
  background-position: -126px -108px;
}

.flag.flag-iq {
  background-position: -108px -72px;
}

.flag.flag-gb {
  background-position: -162px -48px;
}

.flag.flag-lt {
  background-position: 0 -96px;
}

.flag.flag-kg {
  background-position: -270px -72px;
}

.flag.flag-aw {
  background-position: -234px 0;
}

.flag.flag-fk {
  background-position: -72px -48px;
}

.flag.flag-um {
  background-position: -18px -168px;
}

.flag.flag-nu {
  background-position: -90px -120px;
}

.flag.flag-ge {
  background-position: -198px -48px;
}

.flag.flag-sb {
  background-position: -234px -132px;
}

.flag.flag-az {
  background-position: -270px 0;
}

.flag.flag-sc {
  background-position: -252px -132px;
}

.flag.flag-ec {
  background-position: -180px -36px;
}

.flag.flag-bd {
  background-position: -36px -12px;
}

.flag.flag-ls {
  background-position: -270px -84px;
}

.flag.flag-eg {
  background-position: -216px -36px;
}

.flag.flag-gw {
  background-position: -162px -60px;
}

.flag.flag-zm {
  background-position: -18px -180px;
}

.flag.flag-as {
  background-position: -180px 0;
}

.flag.flag-ly {
  background-position: -54px -96px;
}

.flag.flag-cz {
  background-position: -54px -36px;
}

.flag.flag-gu {
  background-position: -144px -60px;
}

.flag.flag-rs {
  background-position: -162px -132px;
}

.flag.flag-ps {
  background-position: -36px -132px;
}
</style>



// WEBPACK FOOTER //
// src/components/UI/KwPhoneInput.vue